import { getFullUserName } from "@/common/helpers/get-full-user-name";
import { components } from "@/api-schema";
import {
  PathsV1UserSearchPostParametersQueryOrderByDirection,
  PathsV1UserSearchPostParametersQueryOrderByField,
  UserStatus,
} from "@/api-schema-enums";
import { useUserByUuidQuery } from "@/common/api/user/use-user-by-uuid.query";
import { useUserSearchQuery } from "@/common/api/user/use-user-search.query";
import { useComboboxPagination } from "@/common/hooks/use-combobox-pagination";
import { Button, ButtonColorVariant, ButtonVariant } from "@/components/ui/button";
import { Combobox, ComboboxOption } from "@/components/ui/combobox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { keepPreviousData } from "@tanstack/react-query";
import { useId, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { VEHICLE_BLOCK_REASONS } from "./_helpers/vehicle-block-reasons";

type VehicleBlockReason = components["schemas"]["VehicleBlockCreateBody"]["reason"];

const MAX_BLOCK_DURATION_DAYS = 14;

interface VehicleBlockDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  blockReason: VehicleBlockReason;
  onBlockReasonChange: (reason: VehicleBlockReason) => void;
  blockDuration: number;
  onBlockDurationChange: (duration: number) => void;
  blockedByUserUuid?: string;
  onBlockedByUserUuidChange?: (uuid: string) => void;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export function VehicleBlockDialog({
  open,
  onOpenChange,
  blockReason,
  onBlockReasonChange,
  blockDuration,
  onBlockDurationChange,
  blockedByUserUuid,
  onBlockedByUserUuidChange,
  onConfirm,
  onCancel,
  isLoading = false,
}: VehicleBlockDialogProps) {
  const { t } = useTranslation(["masterdata", "common"]);
  const reasonId = useId();
  const durationId = useId();
  const blockedById = useId();

  const { searchTerm, debouncedSearchTerm, pageSize, createPaginationState } = useComboboxPagination();

  const showUserPicker = !!onBlockedByUserUuidChange;

  const { data: usersData, isPending: isUsersLoading } = useUserSearchQuery({
    options: {
      parameters: {
        pageSize,
        search: debouncedSearchTerm || undefined,
        orderBy: [
          {
            field: PathsV1UserSearchPostParametersQueryOrderByField.LastName,
            direction: PathsV1UserSearchPostParametersQueryOrderByDirection.Asc,
          },
          {
            field: PathsV1UserSearchPostParametersQueryOrderByField.FirstName,
            direction: PathsV1UserSearchPostParametersQueryOrderByDirection.Asc,
          },
        ],
      },
      body: { status: [UserStatus.Enabled] },
    },
    enabled: showUserPicker,
    queryOptions: { placeholderData: keepPreviousData },
  });

  const { data: selectedUser } = useUserByUuidQuery(blockedByUserUuid ?? "", undefined);

  const userOptionsFromSearch: ComboboxOption[] = useMemo(
    () =>
      (usersData?.data ?? []).map((u) => ({
        value: u.uuid,
        label: getFullUserName({ user: u }),
        searchText: getUserSearchText(u),
      })),
    [usersData?.data],
  );

  const userOptions: ComboboxOption[] = useMemo(() => {
    if (!selectedUser?.uuid) {
      return userOptionsFromSearch;
    }

    const alreadyInList = userOptionsFromSearch.some((o) => o.value === selectedUser.uuid);

    if (alreadyInList) {
      return userOptionsFromSearch;
    }

    return [
      {
        value: selectedUser.uuid,
        label: getFullUserName({ user: selectedUser }),
        searchText: getUserSearchText(selectedUser),
      },
      ...userOptionsFromSearch,
    ];
  }, [userOptionsFromSearch, selectedUser]);

  const userPaginationState = createPaginationState(
    usersData?.meta?.total,
    userOptionsFromSearch.length,
    isUsersLoading,
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-125">
        <DialogHeader>
          <DialogTitle>{t("masterdata:BlockVehicle")}</DialogTitle>
          <DialogDescription>
            {t("masterdata:PleaseSelectReasonAndDurationOfTheBlocking", {
              maxDays: MAX_BLOCK_DURATION_DAYS,
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {showUserPicker && (
            <div className="flex flex-col gap-2">
              <Label htmlFor={blockedById}>{t("masterdata:BlockedBy")}</Label>
              <Combobox
                id={blockedById}
                options={userOptions}
                value={blockedByUserUuid}
                onChange={(value) => onBlockedByUserUuidChange!(value as string)}
                searchValue={searchTerm}
                shouldFilter={false}
                isLoading={userPaginationState.isLoading && userOptions.length === 0}
                hasMore={userPaginationState.hasMore}
                remainingCount={userPaginationState.remainingCount}
                isLoadingMore={userPaginationState.isLoadingMore}
                onLoadMore={userPaginationState.onLoadMore}
                onSearchChange={userPaginationState.onSearchChange}
                placeholder={t("common:SelectUser")}
                searchPlaceholder={t("common:SearchUser")}
                emptyText={t("common:NoUsersFound")}
                disabled={isLoading}
              />
            </div>
          )}

          <div className="flex flex-col gap-2">
            <Label htmlFor={reasonId}>{t("masterdata:BlockReason")}</Label>
            <Select value={blockReason} onValueChange={(value) => onBlockReasonChange(value as VehicleBlockReason)}>
              <SelectTrigger id={reasonId}>
                <SelectValue placeholder={t("common:SelectReason")} />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(VEHICLE_BLOCK_REASONS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {t(label)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor={durationId}>{t("masterdata:BlockDuration")}</Label>
            <Select value={blockDuration.toString()} onValueChange={(value) => onBlockDurationChange(Number(value))}>
              <SelectTrigger id={durationId}>
                <SelectValue placeholder={t("masterdata:SelectDuration")} />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: MAX_BLOCK_DURATION_DAYS }, (_, i) => i + 1).map((day) => (
                  <SelectItem key={day} value={day.toString()}>
                    {day} {day === 1 ? t("masterdata:Day") : t("masterdata:Days")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant={ButtonVariant.Outlined}
            colorVariant={ButtonColorVariant.Secondary}
            onClick={onCancel}
            disabled={isLoading}
          >
            {t("common:Cancel")}
          </Button>
          <Button onClick={onConfirm} disabled={isLoading || (showUserPicker && !blockedByUserUuid)}>
            {isLoading ? t("masterdata:CreatingBlock") : t("masterdata:BlockVehicle")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function getUserSearchText(user: Parameters<typeof getFullUserName>[0]["user"]): string {
  const name = getFullUserName({ user, emailFallback: false, fallback: "" });
  return [name, user.email].filter(Boolean).join(" ");
}
